import { ArrowLeftRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataLoader } from '@/components/ui/DataLoader';
import { FundTransactionStatusBadge } from '@/components/fund-detail/FundTransactionStatusBadge';
import { useInvestorTransfers } from '@/hooks/react-query';
import { formatCurrency, formatDate } from '@/utils/formatters';
import type {
  InvestorTransfer,
  InvestorTransferDirection,
} from '@/types/investor';

const DIRECTION_LABELS: Record<InvestorTransferDirection, string> = {
  TRANSFERRED_IN: 'Transferred in',
  TRANSFERRED_OUT: 'Transferred out',
};

const HEADER_CLASS =
  'px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider';
const CELL_CLASS = 'px-4 py-4 whitespace-nowrap text-sm text-foreground';

export function InvestorTransfersCard({ investorId }: { investorId: string }) {
  const query = useInvestorTransfers(investorId);

  if (query.isLoading) return null;

  return (
    <DataLoader query={query}>
      {(transfers: InvestorTransfer[]) =>
        transfers.length === 0 ? null : (
          <InvestorTransfersTable transfers={transfers} />
        )
      }
    </DataLoader>
  );
}

function InvestorTransfersTable({
  transfers,
}: {
  transfers: InvestorTransfer[];
}) {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowLeftRight className="h-5 w-5" />
          Transfers
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table
            className="min-w-full divide-y divide-border"
            data-testid="investor-transfers-table"
          >
            <thead className="bg-muted">
              <tr>
                <th className={`${HEADER_CLASS} text-left`}>Effective Date</th>
                <th className={`${HEADER_CLASS} text-left`}>Fund</th>
                <th className={`${HEADER_CLASS} text-left`}>Transfer</th>
                <th className={`${HEADER_CLASS} text-left`}>Other LP</th>
                <th className={`${HEADER_CLASS} text-right`}>Commitment</th>
                <th className={`${HEADER_CLASS} text-center`}>Status</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {transfers.map((transfer) => (
                <tr
                  key={transfer.fund_transaction_id}
                  className="hover:bg-accent cursor-pointer"
                  onClick={() =>
                    navigate(
                      `/app/funds/${transfer.fund_id}/fund-transactions/${transfer.fund_transaction_id}`
                    )
                  }
                >
                  <td className={CELL_CLASS}>
                    {formatDate(transfer.effective_date)}
                  </td>
                  <td className={CELL_CLASS}>{transfer.fund_name}</td>
                  <td className={CELL_CLASS}>
                    {DIRECTION_LABELS[transfer.direction]}
                  </td>
                  <td className={CELL_CLASS}>
                    <Link
                      to={`/app/investors/${transfer.counterparty_investor_id}`}
                      className="hover:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {transfer.counterparty_name}
                    </Link>
                  </td>
                  <td className={`${CELL_CLASS} text-right`}>
                    {formatCurrency(transfer.commitment_amount, {
                      currency: transfer.currency,
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </td>
                  <td className={`${CELL_CLASS} text-center`}>
                    <FundTransactionStatusBadge status={transfer.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
